
import React from "react";
import { useNavigate } from "react-router-dom";
import useModuleProgress from "../hooks/useModuleProgress";
import "../styles/pages/_module-pages.scss";
import "../styles/pages/_learn-assessments.scss";
import "../styles/pages/_quiz-completion.scss";

interface ModuleSection {
  title: string;
  content: string[];
  additionalSection?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: "Why Purpose Matters",
    content: [
      "<strong>Learning Goal:</strong> Discover how finding meaning and purpose at work can improve motivation, satisfaction, and well-being.",
      "<br/>",
      "<strong>Key Topics:</strong>",
      "• The importance of purpose in work",
      "• Connecting tasks to a bigger goal",
      "• Making a positive impact",
      "• Celebrating achievements and progress",
      "<br/>",
      "<strong>Discussion:</strong> When you feel your work has meaning, you are more likely to be motivated and satisfied. Purpose gives you direction and helps you overcome challenges."
    ]
  },
  {
    title: "How to Find Purpose",
    content: [
      "Finding purpose at work is a personal journey. Here are some ways to discover and nurture it:",
      "<br/>",
      "<strong>Reflection:</strong>",
      "• Reflect on what you enjoy and value in your work",
      "• Connect your tasks to a bigger goal or mission",
      "• Look for ways to make a positive impact",
      "• Celebrate achievements and progress",
      "<br/>",
      "<strong>Mini Practice:</strong> Write down one thing you did at work this week that made a difference, no matter how small."
    ]
  },
  {
    title: "Reflection",
    content: [
      "Ask yourself:",
      "• What makes your work meaningful to you?",
      "• How can you find more purpose in your daily tasks?",
      "<br/>",
      "<strong>Journal Prompt:</strong> 'Today, I found purpose in my work when…'"
    ]
  },
  {
    title: "Ready to Test Your Knowledge?",
    content: [
      "You've completed Module 5: Finding Meaning and Purpose at Work! Now, let's reinforce your learning with a quick quiz."
    ],
    additionalSection: {
      title: "Next Steps",
      content: [
        "Click 'Take the Quiz' below to test your knowledge about finding meaning and purpose at work.",
        "The quiz will help you review key concepts about building purpose in your career."
      ]
    }
  }
];

const OccupationalModule5Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress("occupationalM5");
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
  navigate("/panel/occupational-module-5/quiz");
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate("/panel/occupational-resources");
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left occupational-panel"
        >
          <div className="module-header">
            <h1>Module 5</h1>
            <p className="module-subtitle">Finding Meaning and Purpose at Work</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Discover how finding meaning and purpose at work can improve motivation, satisfaction, and well-being.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? "Exit Module" : "Previous"}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate("/panel/occupational-module-5/quiz")}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupationalModule5Page;


